import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';

import { useClients } from '@/clients/queries';
import { DataTable } from '@/components/data-table';
import { PageShell } from '@/components/page-shell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useListQueryRoute } from '@/lib/use-list-query-route';
import {
  useTradeLinks,
  type TradeLinkRecord,
  type TradeLinkSort,
  type TradeLinksListFilter,
} from '@/trade-links/queries';
import { TradeLinkImportDialog } from '@/trade-links/trade-link-import-dialog';

const ALL_CLIENTS = '__all__';

export const TradeLinksListPage = () => {
  // Phase 8.5-05 — URL-driven list state (ADR-014).
  const lq = useListQueryRoute<TradeLinksListFilter, TradeLinkSort>({
    from: '/_protected/trade-links',
    defaultFilter: {},
    defaultSort: { field: 'createdAt', dir: 'desc' },
  });
  const [importOpen, setImportOpen] = useState(false);

  const { data: clients } = useClients();
  const clientsNameById = new Map((clients ?? []).map((c) => [c.id, c.name]));

  const tradeLinksQuery = useTradeLinks({
    filter: lq.filter,
    sort: lq.sort,
    page: lq.page,
    pageSize: lq.pageSize,
  });
  const items = tradeLinksQuery.data?.items ?? [];
  const total = tradeLinksQuery.data?.total ?? 0;

  const columns: ColumnDef<TradeLinkRecord>[] = [
    {
      id: 'partnerSteamId',
      header: 'Partner Steam ID',
      cell: ({ row }) => (
        <Link
          to="/trade-links/$tradeLinkId"
          params={{ tradeLinkId: row.original.id }}
          className="font-mono text-xs hover:underline"
        >
          {row.original.partnerSteamId}
        </Link>
      ),
    },
    {
      id: 'token',
      header: 'Token',
      cell: ({ row }) => <span className="font-mono text-xs">{row.original.token}</span>,
    },
    {
      id: 'source',
      header: 'Источник',
      meta: { sortField: 'source' },
      cell: ({ row }) => <Badge variant="secondary">{row.original.source}</Badge>,
    },
    {
      id: 'client',
      header: 'Клиент',
      cell: ({ row }) => clientsNameById.get(row.original.clientId) ?? row.original.clientId,
    },
    {
      id: 'createdAt',
      header: 'Создан',
      meta: { sortField: 'createdAt' },
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {new Date(row.original.createdAt).toLocaleString('ru-RU')}
        </span>
      ),
    },
  ];

  const filterSlot = (
    <>
      <label className="flex items-center gap-2 text-sm text-muted-foreground">
        <input
          type="checkbox"
          checked={lq.filter.includeDeleted ?? false}
          onChange={(e) => {
            lq.updateQuery({
              filter: { includeDeleted: e.target.checked ? true : undefined },
            });
          }}
          className="h-4 w-4"
        />
        Показывать удалённых
      </label>
      <div className="w-48">
        <Select
          value={lq.filter.clientId ?? ALL_CLIENTS}
          onValueChange={(v) => {
            lq.updateQuery({ filter: { clientId: v === ALL_CLIENTS ? undefined : v } });
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_CLIENTS}>Все клиенты</SelectItem>
            {(clients ?? []).map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <span className="text-sm text-muted-foreground">Всего: {total}</span>
    </>
  );

  return (
    <div className="space-y-4 p-6">
      <PageShell
        title="Trade-links"
        actions={
          <Button
            onClick={() => {
              setImportOpen(true);
            }}
          >
            Импорт
          </Button>
        }
      >
        {tradeLinksQuery.isLoading ? (
          <div className="p-8 text-center text-muted-foreground">Загрузка…</div>
        ) : (
          <DataTable
            columns={columns}
            data={items}
            emptyMessage="Нет trade-links"
            filterSlot={filterSlot}
            sort={{ field: lq.sort.field, dir: lq.sort.dir }}
            onSortChange={(next) => {
              lq.updateQuery({
                sort: { field: next.field as TradeLinkSort | null, dir: next.dir },
              });
            }}
            pagination={{
              page: lq.page,
              pageSize: lq.pageSize,
              total,
              onPageChange: (page) => {
                lq.updateQuery({ page });
              },
            }}
          />
        )}
      </PageShell>

      <TradeLinkImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        {...(lq.filter.clientId ? { defaultClientId: lq.filter.clientId } : {})}
      />
    </div>
  );
};
